import type { ForwardedRef } from 'react';
import type { Middleware } from 'swr';

import { useImperativeHandle, useMemo } from 'react';
import { useCoreStoreDispatch } from '@ricoh/ria.client.core/plugins/store/hooks/dispatch';
import { componentDialogSliceActions } from '@ricoh/ria.client.core/plugins/store/slices/component/dialog';

import type { SwrProviderHooks, SwrProviderProps, SwrProviderRef } from './_.type';

import { createErrorMiddleware } from '../middleware/_error';
import { createLoggerMiddleware } from '../middleware/_logger';
import { createSessionMiddleware } from '../middleware/_session';

/**
 * Use SwrProvider hooks.
 *
 * @param {SwrProviderProps} _props SwrProvider props.
 * @param {SwrProviderRef} ref SwrProvider ref.
 *
 * @returns {SwrProviderHooks} SwrProvider hooks.
 */
export default function useSwrProviderHooks(_props: SwrProviderProps, ref: ForwardedRef<SwrProviderRef>): SwrProviderHooks {
  const dispatch = useCoreStoreDispatch();

  const loggerMiddleware = useMemo<Middleware>(() => createLoggerMiddleware(), []);

  const errorMiddleware = useMemo<Middleware>(
    () =>
      createErrorMiddleware(() => {
        dispatch(componentDialogSliceActions.network_setShowNetworkError(true));
      }),
    [dispatch],
  );

  const sessionMiddleware = useMemo<Middleware>(
    () =>
      createSessionMiddleware(() => {
        dispatch(componentDialogSliceActions.session_reset());
      }),
    [dispatch],
  );

  useImperativeHandle(ref, () => ({}));

  return useMemo<SwrProviderHooks>(
    () => ({
      refs: {},

      states: {
        middlewares: [sessionMiddleware, errorMiddleware, loggerMiddleware],
      },

      events: {},
    }),
    [errorMiddleware, loggerMiddleware, sessionMiddleware],
  );
}
